import React from 'react';
import { Comp } from './comp.type';
import { Division, LineItem } from '../glossary/glossary.type';
import { deepCopy } from 'common/utils/common.util';
import { handleError } from 'common/utils/util.errors';
import { deleteCompLineItem, getComp, updateComp, updateCompLineItem } from './comp.api';
import CompRenderer from './CompRenderer';
import { toast } from 'react-toastify';
import { updateLineItem } from '../glossary/glossary.api';
import { getProjectDraws } from '../project/project.api';
import { ProjectDraw } from '../project/project.type';

interface Props {
  initialComp: Comp;
  divisions: Division[];
}
const CompWrapper: React.FC<Props> = ({ initialComp, divisions }) => {
  const [comp, setComp] = React.useState<Comp>(deepCopy(initialComp));
  const [isCompDirty, setIsCompDirty] = React.useState(false);
  const [isCompLoading, setIsCompLoading] = React.useState(false);
  const [compDraws, setCompDraws] = React.useState<ProjectDraw[]>([]);

  React.useEffect(() => {
    setComp(deepCopy(initialComp));
    setIsCompDirty(false);
  }, [initialComp]);

  React.useEffect(() => {
    fetchProjectDraws(comp.project.projectId).then((projectDraws: ProjectDraw[]) => {
      setCompDraws(projectDraws);
    });
  }, [comp.project.projectId]);

  const updateCompLocally = (updatedComp: Comp) => {
    setComp(updatedComp);
    setIsCompDirty(true);
  };

  const resetProjectAnalysis = () => {
    setComp(deepCopy(initialComp));
    setIsCompDirty(false);
  };

  const saveProjectAnalysis = async () => {
    const totalBudget = comp.lineItems.filter(li => !li.isDeleted).reduce((acc, row) => acc + row.totalBudget, 0);
    const unassignedBudget = comp.lineItems.filter(li => !li.isDeleted && !li.division).reduce((acc, row) => acc + row.totalBudget, 0);
    try {
      setIsCompLoading(true);
      await updateComp(comp.id, {
        name: comp.name,
        grossBuildingSF: comp.grossBuildingSF,
        netRentableSF: comp.netRentableSF,
        siteAcreage: comp.siteAcreage,
        unitCount: comp.unitCount,
        buildingCount: comp.buildingCount,
        storyCount: comp.storyCount,
        budget: totalBudget,
        unassignedBudget: unassignedBudget,
      });
      const pendingUpdates: Promise<any>[] = [];
      const deletedLineItems = comp.lineItems.filter(lineItem => lineItem?.isDeleted);
      deletedLineItems.forEach(lineItem => {
        pendingUpdates.push(deleteCompLineItem(comp.id, lineItem.id));
      });
      const editedLineItems = comp.lineItems.filter(lineItem => lineItem?.isEdited);
      editedLineItems.forEach(lineItem => {
        pendingUpdates.push(updateCompLineItem(comp.id, lineItem.id, { ...lineItem, divisionId: lineItem.division?.id ?? null }));
        if (lineItem.updateDivisionGlobally) {
          const newGlobalLineItem = {
            ...lineItem.lineItem,
            divisionId: lineItem.division?.id ?? null,
          } as LineItem;
          pendingUpdates.push(updateLineItem(lineItem.lineItem.id, newGlobalLineItem));
        }
      });
      await Promise.all(pendingUpdates);
      toast.success('All Changes Saved');
      setIsCompDirty(false);
      const updatedComp = await getComp(comp.id);
      setComp(updatedComp);
    } catch (error) {
      handleError(error);
    } finally {
      setIsCompLoading(false);
    }
  };

  const refetchProjectAnalysis = async () => {
    try {
      setIsCompLoading(true);
      const updatedComp = await getComp(comp.id);
      setComp(updatedComp);
    } catch (error) {
      handleError(error);
    } finally {
      setIsCompLoading(false);
    }
  };

  return (
    <CompRenderer
      comp={comp}
      updateComp={updateCompLocally}
      resetComp={resetProjectAnalysis}
      saveComp={saveProjectAnalysis}
      refetchComp={refetchProjectAnalysis}
      isCompDirty={isCompDirty}
      isCompLoading={isCompLoading}
      divisions={divisions}
      compDraws={compDraws}
    />
  );
};

export default CompWrapper;

const fetchProjectDraws = async (projectId: string) => {
  try {
    const draws = await getProjectDraws(projectId);
    return draws;
  } catch (error) {
    handleError(error);
    return [];
  }
};
